import { Search, Download, SlidersHorizontal } from "lucide-react";
import { Button } from "../ui/button";
import { useState } from "react";

export const UserFilterBar = ({
  searchQuery, setSearchQuery,
  filters, setFilters,
  filteredCount, totalCount,
  onExportCSV, onExportNoPurchaseCSV, onExportJSON,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const hasActiveFilters = filters.customerType !== "all" || filters.period !== "all" || filters.hasPhone !== "all" || filters.receivedKit !== "all";

  return (
    <div data-testid="admin-filter-bar" className="bg-white dark:bg-[#1a231a] border border-[#2C3329]/10 dark:border-[#2a3a2a] rounded-sm p-4 sm:p-5 mb-6">
      {/* Search + Toggle */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A3A3A3]" />
          <input
            data-testid="admin-search-input"
            type="text"
            placeholder="חיפוש לפי שם, אימייל או טלפון..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pr-10 pl-4 py-2.5 rounded-sm border border-[#2C3329]/15 dark:border-[#2a3a2a] bg-[#FAF8F5] dark:bg-[#121812] text-[#2C3329] dark:text-white font-sans text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D43] focus:border-[#4A5D43] transition-colors"
          />
        </div>
        <Button
          data-testid="toggle-filters-btn"
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          className={`rounded-sm border-[#2C3329]/15 hover:bg-[#2C3329]/5 transition-colors ${hasActiveFilters ? 'border-[#4A5D43] bg-[#4A5D43]/5' : ''}`}
        >
          <SlidersHorizontal className="h-4 w-4" />
          {hasActiveFilters && <span className="w-1.5 h-1.5 rounded-full bg-[#4A5D43] ms-1.5" />}
        </Button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-[#2C3329]/8 dark:border-[#2a3a2a]">
          <FilterSelect
            testId="filter-type-dropdown"
            value={filters.customerType}
            onChange={(v) => setFilters({...filters, customerType: v})}
            options={[
              { value: "all", label: "כל הלקוחות" },
              { value: "new", label: "חדשים (שבוע)" },
              { value: "vip", label: "VIP" },
              { value: "no_purchase", label: "לא רכשו" },
            ]}
          />
          <FilterSelect
            testId="filter-period-dropdown"
            value={filters.period}
            onChange={(v) => setFilters({...filters, period: v})}
            options={[
              { value: "all", label: "כל הזמנים" },
              { value: "today", label: "היום" },
              { value: "week", label: "השבוע" },
              { value: "month", label: "החודש" },
            ]}
          />
          <FilterSelect
            testId="filter-phone-dropdown"
            value={filters.hasPhone}
            onChange={(v) => setFilters({...filters, hasPhone: v})}
            options={[
              { value: "all", label: "טלפון: הכל" },
              { value: "yes", label: "יש טלפון" },
              { value: "no", label: "אין טלפון" },
            ]}
          />
          <FilterSelect
            testId="filter-kit-dropdown"
            value={filters.receivedKit}
            onChange={(v) => setFilters({...filters, receivedKit: v})}
            options={[
              { value: "all", label: "ערכה: הכל" },
              { value: "yes", label: "קיבל ערכה" },
              { value: "no", label: "לא קיבל" },
            ]}
          />
          {hasActiveFilters && (
            <button
              data-testid="clear-filters-btn"
              onClick={() => setFilters({ hasPhone: "all", receivedKit: "all", period: "all", customerType: "all" })}
              className="text-xs text-[#C67D63] hover:text-[#a5604c] font-medium px-2 transition-colors"
            >
              נקה הכל
            </button>
          )}
        </div>
      )}

      {/* Results count + Export */}
      <div className="flex flex-wrap items-center justify-between mt-4 pt-4 border-t border-[#2C3329]/8 dark:border-[#2a3a2a]">
        <span className="font-sans text-sm text-[#5C5C5C] dark:text-[#9A9790]">
          {filteredCount === totalCount ? `${totalCount} משתמשים` : `${filteredCount} מתוך ${totalCount}`}
        </span>
        <div className="flex gap-2">
          <Button data-testid="export-no-purchase-btn" variant="ghost" size="sm" onClick={onExportNoPurchaseCSV} className="text-xs text-[#5C5C5C] hover:text-[#2C3329] hover:bg-[#2C3329]/5 rounded-sm">
            <Download className="h-3 w-3 me-1" />לא רכשו
          </Button>
          <Button data-testid="export-csv-btn" variant="ghost" size="sm" onClick={onExportCSV} className="text-xs text-[#5C5C5C] hover:text-[#2C3329] hover:bg-[#2C3329]/5 rounded-sm">
            <Download className="h-3 w-3 me-1" />CSV
          </Button>
          <Button data-testid="export-json-btn" variant="ghost" size="sm" onClick={onExportJSON} className="text-xs text-[#5C5C5C] hover:text-[#2C3329] hover:bg-[#2C3329]/5 rounded-sm">
            <Download className="h-3 w-3 me-1" />JSON
          </Button>
        </div>
      </div>
    </div>
  );
};

const FilterSelect = ({ testId, value, onChange, options }) => (
  <select
    data-testid={testId}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="px-3 py-2 rounded-sm border border-[#2C3329]/15 dark:border-[#2a3a2a] bg-[#FAF8F5] dark:bg-[#121812] font-sans text-sm text-[#2C3329] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#4A5D43] cursor-pointer"
  >
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);
